"use client";

import type React from "react";
import { Component, createElement, isValidElement } from "react";
import type { CaptureContext } from "../../lib/types";
import { describeThrownValue, isErrorLike, normalizeError } from "../normalize-error";
import { captureException, getDatadogReferenceId } from "../tracing";

type ErrorBoundaryRenderArgs = {
	error: unknown;
	componentStack: string;
	eventId: string | null;
	resetError(): void;
};

type ErrorBoundaryProps = {
	children?: React.ReactNode;
	fallback?: React.ReactElement | ((errorData: ErrorBoundaryRenderArgs) => React.ReactElement);
	onError?: (error: unknown, componentStack: string | undefined, eventId: string | null) => void;
	onReset?: (error: unknown, componentStack: string | null | undefined, eventId: string | null) => void;
};

type ErrorBoundaryState = {
	error: unknown | null;
	componentStack: string | null;
	eventId: string | null;
};

class InternalErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	public state: ErrorBoundaryState = {
		error: null,
		componentStack: null,
		eventId: null,
	};

	public componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
		const context: CaptureContext = {
			extra: {
				componentStack: errorInfo.componentStack,
				...(isErrorLike(error) ? {} : describeThrownValue(error)),
			},
		};

		const referenceId = captureException(normalizeError(error), context);
		const eventId = referenceId ?? getDatadogReferenceId() ?? null;

		this.setState({
			error,
			componentStack: errorInfo.componentStack,
			eventId,
		});

		this.props.onError?.(error, errorInfo.componentStack, eventId);
	}

	private readonly resetErrorBoundary = (): void => {
		const { error, componentStack, eventId } = this.state;
		this.props.onReset?.(error, componentStack, eventId);
		this.setState({ error: null, componentStack: null, eventId: null });
	};

	public render(): React.ReactNode {
		const { fallback, children } = this.props;
		const { error, componentStack, eventId } = this.state;

		if (error) {
			if (typeof fallback === "function") {
				return fallback({
					error,
					componentStack: componentStack ?? "",
					eventId: eventId ?? null,
					resetError: this.resetErrorBoundary,
				});
			}

			if (isValidElement(fallback)) {
				return fallback;
			}

			return null;
		}

		return children;
	}
}

export const ErrorBoundary = ({ children, ...props }: ErrorBoundaryProps): React.ReactNode => {
	return createElement(InternalErrorBoundary, props, children);
};
